import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { LEGACY_PIPELINE, getLegacyPipelineIdx } from "@/components/StatusPipeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const fmtDT   = d => d ? new Date(d).toLocaleString("en-IN",  { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CHARGER_ICONS = { "Small Charger":"🔋", "Fast Charger":"⚡", "Rickshaw Charger":"🛺" };

const WARRANTY_BADGE = {
  "Under Warranty":  { bg:"#f0fdf4", text:"#15803d", border:"#86efac" },
  "Out of Warranty": { bg:"#fff7ed", text:"#c2410c", border:"#fed7aa" },
  "Expired":         { bg:"#fff1f2", text:"#be123c", border:"#fda4af" },
  "XP Charger":      { bg:"#eff6ff", text:"#1d4ed8", border:"#bfdbfe" },
};

const STATUS_DOT_COLORS = {
  "Pick Up Request":  "#7c3aed",
  "Pick Up Accepted": "#4f46e5",
  "Barcode Generated":"#0891b2",
  "Picked Up":        "#0284c7",
  "In Transit":       "#1d4ed8",
  "Received":         "#475569",
  "Repairing":        "#d97706",
  "In Dispatch":      "#ea580c",
};
const DEFAULT_DOT = "#16a34a";

export default function SCPrintJobCard() {
  const { scId } = useParams();
  const [job,     setJob]     = useState(null);
  const [sc,      setSC]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`${API}/sc/jobs/${scId}`)
         .then(r => setJob(r.data))
         .catch(() => setError("SC Job not found")),
      axios.get(`${API}/settings`)
           .then(r => setSC(r.data))
           .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [scId]);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
      <div style={{ width:"36px", height:"36px", borderRadius:"50%", border:"3px solid #16a34a", borderTopColor:"transparent", animation:"spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
  if (error || !job) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
      <p style={{ color:"#64748b", fontSize:"14px" }}>{error || "SC Job not found"}</p>
    </div>
  );

  const companyName  = sc?.name     || "XSTRONG POWER EV CHARGER";
  const tagline      = sc?.tagline  || "EV Charger Repair & Service Center";
  const trackingUrl  = `${window.location.origin}/sc-track?job=${job.sc_no}`;
  const chargers     = job.chargers || [];
  const specs        = job.specs    || {};
  const cust         = job.customer || {};
  const courier      = job.courier_details;

  const currentIdx   = getLegacyPipelineIdx(job.current_status, job.status_history || []);
  const currentStep  = LEGACY_PIPELINE[currentIdx] || LEGACY_PIPELINE[0];
  const statusColor  = STATUS_DOT_COLORS[job.current_status] || DEFAULT_DOT;

  // Full address string
  const addressParts = [cust.address, cust.landmark ? `Near: ${cust.landmark}` : null, cust.city, cust.state, cust.pincode].filter(Boolean);

  return (
    <>
      {/* ── Print toolbar (hidden on print) ──────────────────────────────── */}
      <div className="no-print" style={{
        position:"fixed", top:0, left:0, right:0, zIndex:50,
        background:"white", borderBottom:"1px solid #e2e8f0",
        padding:"10px 20px", display:"flex", alignItems:"center", justifyContent:"space-between",
        boxShadow:"0 1px 4px rgba(0,0,0,0.08)"
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <img src="/logo.jpg" alt="logo" style={{ height:"34px", width:"auto", objectFit:"contain" }}/>
          <div>
            <p style={{ margin:0, fontSize:"13px", fontWeight:700, color:"#0f172a", fontFamily:"'Space Grotesk',sans-serif" }}>
              SC Job Card — {job.sc_no}
            </p>
            <p style={{ margin:0, fontSize:"11px", color:"#94a3b8" }}>
              Print &amp; attach to the charger
            </p>
          </div>
        </div>
        <div style={{ display:"flex", gap:"8px" }}>
          <button onClick={() => window.history.back()}
            style={{ fontSize:"13px", color:"#64748b", padding:"6px 14px", border:"1px solid #e2e8f0", borderRadius:"8px", background:"white", cursor:"pointer" }}>
            ← Back
          </button>
          <button onClick={() => window.print()}
            style={{ fontSize:"13px", fontWeight:700, color:"white", background:"#16a34a", padding:"6px 18px", border:"none", borderRadius:"10px", cursor:"pointer" }}
            data-testid="sc-job-card-print-btn">
            🖨️ Print Job Card
          </button>
        </div>
      </div>

      {/* ── Page wrapper ─────────────────────────────────────────────────── */}
      <div style={{ paddingTop:"64px", background:"#f0fdf4", minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", padding:"64px 16px 40px" }}>

        {/* ── A5 Job Card ──────────────────────────────────────────────── */}
        <div style={{
          width:"148mm", background:"#ffffff",
          borderRadius:"12px", overflow:"hidden",
          boxShadow:"0 4px 24px rgba(0,0,0,0.10)",
          fontFamily:"'Inter', -apple-system, sans-serif",
        }} data-testid="sc-job-card-root">

          {/* GREEN HEADER */}
          <div style={{ background:"#16a34a", padding:"14px 16px 12px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                <div style={{ background:"white", borderRadius:"8px", padding:"4px 8px" }}>
                  <img src="/logo.jpg" alt="logo" style={{ height:"28px", width:"auto", objectFit:"contain" }}/>
                </div>
                <div>
                  <p style={{ fontSize:"13px", fontWeight:800, color:"white", margin:0, fontFamily:"'Space Grotesk',sans-serif" }}>{companyName}</p>
                  <p style={{ fontSize:"9px", color:"rgba(255,255,255,0.8)", margin:0 }}>{tagline}</p>
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <p style={{ fontSize:"18px", fontFamily:"monospace", fontWeight:800, color:"white", margin:0, letterSpacing:"-0.02em" }}>
                  {job.sc_no}
                </p>
                <p style={{ fontSize:"9px", color:"rgba(255,255,255,0.75)", margin:"2px 0 0" }}>
                  Received: {fmtDate(job.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* STATUS BAR */}
          <div style={{ background: statusColor, padding:"6px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <p style={{ fontSize:"11px", fontWeight:700, color:"white", margin:0 }}>
              STATUS: {job.current_status?.toUpperCase()}
            </p>
            <p style={{ fontSize:"9px", color:"rgba(255,255,255,0.85)", margin:0 }}>
              Updated: {fmtDate(job.updated_at)}
            </p>
          </div>

          {/* BODY */}
          <div style={{ padding:"14px 16px" }}>

            {/* Customer Details */}
            <div style={{ border:"1px solid #e2e8f0", borderRadius:"8px", padding:"10px", marginBottom:"12px" }}>
              <p style={{ fontSize:"9px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 7px" }}>
                👤 Customer Details
              </p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 12px" }}>
                <div>
                  <p style={{ fontSize:"12px", fontWeight:700, color:"#1e293b", margin:"0 0 2px" }}>{cust.name || "—"}</p>
                  <p style={{ fontSize:"10px", color:"#374151", margin:"0 0 1px" }}>📞 {cust.phone || "—"}</p>
                  {cust.alt_phone && <p style={{ fontSize:"10px", color:"#64748b", margin:"0 0 1px" }}>Alt: {cust.alt_phone}</p>}
                </div>
                {addressParts.length > 0 && (
                  <div>
                    <p style={{ fontSize:"8px", color:"#94a3b8", margin:"0 0 2px" }}>ADDRESS</p>
                    <p style={{ fontSize:"9px", color:"#374151", margin:0, lineHeight:1.4 }}>
                      {addressParts.join(", ")}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Charger Details */}
            {chargers.length > 0 && (
              <div style={{ border:"1px solid #e2e8f0", borderRadius:"8px", padding:"10px", marginBottom:"12px" }}>
                <p style={{ fontSize:"9px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 7px" }}>
                  ⚡ Charger Details ({chargers.length} unit{chargers.length !== 1 ? "s" : ""})
                </p>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ borderBottom:"1px solid #f1f5f9" }}>
                      {["#","Type","Model","Serial No.","Body","Warranty","Mfg"].map(h => (
                        <th key={h} style={{ textAlign:"left", fontSize:"7.5px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", padding:"3px 5px", letterSpacing:"0.04em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chargers.map((c, i) => {
                      const mfg = [c.mfg_month ? MONTHS_SHORT[parseInt(c.mfg_month) - 1] : null, c.mfg_year].filter(Boolean).join(" ");
                      const wc  = WARRANTY_BADGE[c.warranty_status];
                      return (
                        <tr key={i} style={{ borderBottom: i < chargers.length - 1 ? "1px solid #f8fafc" : "none" }}>
                          <td style={{ padding:"4px 5px", fontSize:"9px", color:"#94a3b8", fontWeight:600 }}>{i + 1}</td>
                          <td style={{ padding:"4px 5px", fontSize:"9px", color:"#374151", whiteSpace:"nowrap" }}>
                            {CHARGER_ICONS[c.charger_type] || "⚡"} {c.charger_type || "—"}
                          </td>
                          <td style={{ padding:"4px 5px", fontSize:"10px", fontFamily:"monospace", fontWeight:700, color:"#16a34a", whiteSpace:"nowrap" }}>
                            {c.charger_spec || "—"}
                          </td>
                          <td style={{ padding:"4px 5px", fontSize:"9px", fontFamily:"monospace", color:"#1e293b" }}>
                            {c.serial_number || "—"}
                          </td>
                          <td style={{ padding:"4px 5px", fontSize:"9px", color:"#374151" }}>{c.body_type || "—"}</td>
                          <td style={{ padding:"4px 5px" }}>
                            {c.warranty_status ? (
                              <span style={{
                                fontSize:"8px", fontWeight:700, padding:"1px 5px", borderRadius:"10px",
                                background: wc?.bg || "#f8fafc", color: wc?.text || "#475569",
                                border:`1px solid ${wc?.border || "#e2e8f0"}`, whiteSpace:"nowrap"
                              }}>
                                {c.warranty_status}
                              </span>
                            ) : "—"}
                          </td>
                          <td style={{ padding:"4px 5px", fontSize:"9px", color:"#94a3b8", whiteSpace:"nowrap" }}>{mfg || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Fault Description */}
            {specs.fault_description && (
              <div style={{ border:"1px solid #fde68a", background:"#fffbeb", borderRadius:"8px", padding:"8px 10px", marginBottom:"12px" }}>
                <p style={{ fontSize:"8px", fontWeight:700, color:"#92400e", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 4px" }}>
                  🔧 Fault / Problem
                </p>
                <p style={{ fontSize:"9px", color:"#1e293b", margin:0, lineHeight:1.4 }}>
                  {specs.fault_description.slice(0, 180)}{specs.fault_description.length > 180 ? "…" : ""}
                </p>
              </div>
            )}

            {/* Courier Details (if filled) */}
            {courier?.awb_number && (
              <div style={{ border:"1px solid #bfdbfe", background:"#eff6ff", borderRadius:"8px", padding:"8px 10px", marginBottom:"12px" }}>
                <p style={{ fontSize:"8px", fontWeight:700, color:"#1e40af", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 5px" }}>
                  🚚 Courier / Dispatch
                </p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px" }}>
                  {courier.courier_name && (
                    <div>
                      <p style={{ fontSize:"8px", color:"#3b82f6", margin:"0 0 1px" }}>Courier</p>
                      <p style={{ fontSize:"10px", fontWeight:700, color:"#1e293b", margin:0 }}>{courier.courier_name}</p>
                    </div>
                  )}
                  <div>
                    <p style={{ fontSize:"8px", color:"#3b82f6", margin:"0 0 1px" }}>AWB / Tracking No.</p>
                    <p style={{ fontSize:"10px", fontFamily:"monospace", fontWeight:700, color:"#1d4ed8", margin:0 }}>{courier.awb_number}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Service Journey Timeline */}
            <div style={{ border:"1px solid #e2e8f0", borderRadius:"8px", padding:"10px", marginBottom:"12px" }}>
              <p style={{ fontSize:"9px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 7px" }}>
                🗺️ Service Journey — Step {currentIdx + 1} of {LEGACY_PIPELINE.length}
              </p>

              {/* Progress bar */}
              <div style={{ height:"4px", background:"#f1f5f9", borderRadius:"4px", overflow:"hidden", marginBottom:"7px" }}>
                <div style={{
                  height:"100%", background:"#16a34a", borderRadius:"4px",
                  width:`${((currentIdx + 1) / LEGACY_PIPELINE.length * 100).toFixed(0)}%`,
                }}/>
              </div>

              {/* Steps grid — deduplicated for display */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(9,1fr)", gap:"2px", textAlign:"center" }}>
                {LEGACY_PIPELINE.map((step, i) => {
                  const done    = i < currentIdx;
                  const current = i === currentIdx;
                  const dot     = STATUS_DOT_COLORS[step.status] || DEFAULT_DOT;
                  return (
                    <div key={`${step.status}-${i}`} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" }}>
                      <div style={{
                        width:"18px", height:"18px", borderRadius:"50%",
                        background: current ? dot : done ? "#16a34a" : "#e2e8f0",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        border: current ? `2px solid ${dot}` : "none",
                        boxShadow: current ? `0 0 0 2px ${dot}30` : "none",
                        flexShrink:0,
                      }}>
                        {current ? <span style={{ fontSize:"9px" }}>{step.emoji}</span>
                          : done  ? <span style={{ color:"white", fontSize:"8px", fontWeight:700 }}>✓</span>
                          : null}
                      </div>
                      <p style={{
                        fontSize:"6px", lineHeight:1.2, margin:0,
                        color: current ? dot : done ? "#15803d" : "#cbd5e1",
                        fontWeight: current ? 700 : done ? 600 : 400,
                        maxWidth:"22px", textAlign:"center", wordBreak:"break-word",
                      }}>
                        {step.status.split(" ").slice(0,2).join(" ")}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Current status highlight */}
              <div style={{ marginTop:"7px", padding:"5px 8px", borderRadius:"6px", background:`${statusColor}18`, border:`1px solid ${statusColor}40` }}>
                <p style={{ fontSize:"9px", fontWeight:700, margin:0, color: statusColor }}>
                  {currentStep.emoji} Current: {job.current_status}
                </p>
                {(job.status_history || []).slice(-3).reverse().map((h, i) => (
                  <p key={i} style={{ fontSize:"7.5px", color:"#94a3b8", margin:"2px 0 0" }}>
                    {h.status} — {fmtDT(h.timestamp)}{h.notes ? ` · ${h.notes}` : ""}
                  </p>
                ))}
              </div>
            </div>

            {/* Barcode + QR */}
            <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"12px" }}>
              <div style={{ flex:1, border:"1px solid #e2e8f0", borderRadius:"8px", padding:"8px", textAlign:"center" }}>
                <p style={{ fontSize:"8px", color:"#94a3b8", margin:"0 0 4px" }}>SCAN BARCODE</p>
                <Barcode value={job.sc_no} width={1.4} height={36} fontSize={10} margin={2} background="#ffffff" displayValue={true}/>
              </div>
              <div style={{ border:"1px solid #bbf7d0", background:"#f0fdf4", borderRadius:"8px", padding:"10px", textAlign:"center", flexShrink:0 }}>
                <p style={{ fontSize:"8px", color:"#15803d", fontWeight:600, margin:"0 0 5px" }}>TRACK ONLINE</p>
                <QRCodeSVG value={trackingUrl} size={70} level="M" includeMargin={false}/>
                <p style={{ fontSize:"7px", color:"#94a3b8", margin:"4px 0 0", wordBreak:"break-all", maxWidth:"80px", lineHeight:1.3 }}>
                  {trackingUrl}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div style={{ borderTop:"1px solid #e2e8f0", paddingTop:"8px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                {sc?.phone && <p style={{ fontSize:"9px", color:"#64748b", margin:"0 0 2px" }}>📞 {sc.phone}{sc.phone2 ? ` / ${sc.phone2}` : ""}</p>}
                {sc?.email && <p style={{ fontSize:"9px", color:"#64748b", margin:0 }}>✉️ {sc.email}</p>}
              </div>
              <div style={{ textAlign:"right" }}>
                <p style={{ fontSize:"8px", color:"#94a3b8", margin:"0 0 2px" }}>SC Job: <strong style={{ fontFamily:"monospace" }}>{job.sc_no}</strong></p>
                <p style={{ fontSize:"8px", color:"#94a3b8", margin:0 }}>Printed: {fmtDate(new Date().toISOString())}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions (no-print) */}
        <div className="no-print" style={{ width:"148mm", marginTop:"16px" }}>
          <div style={{ background:"white", borderRadius:"12px", padding:"14px 16px", border:"1px solid #e2e8f0" }}>
            <p style={{ fontSize:"12px", fontWeight:600, color:"#374151", margin:"0 0 8px" }}>📌 How to use this SC Job Card:</p>
            <ol style={{ margin:0, paddingLeft:"16px", fontSize:"11px", color:"#64748b", lineHeight:2 }}>
              <li>Print this card on paper (A5 size recommended)</li>
              <li>Attach / tape to the charger before storage or repair</li>
              <li>Customer scans the QR code to track their SC job online</li>
              <li>Staff scans the barcode to pull up the SC job instantly</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A5 portrait; margin: 5mm; }
          .no-print { display: none !important; }
          body { background: white !important; padding: 0 !important; margin: 0 !important; }
        }
      `}</style>
    </>
  );
}
